<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50 overflow-y-auto">
    <div class="my-8 bg-white rounded-lg p-6 max-w-2xl w-full relative">
      <!-- Modal Content -->
      <div class="max-h-[calc(100vh-8rem)] overflow-y-auto">
        <div class="flex justify-between items-center sticky top-0 bg-white pb-4 mb-6 border-b">
          <h2 class="text-xl font-bold">Elije las opciones estandar para la checklist</h2>
        </div>

        <div class="space-y-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Preset Options -->
            <button
              v-for="preset in optionPresets"
              :key="preset.name"
              @click="selectPreset(preset)"
              class="p-4 border rounded-lg text-left transition-colors duration-200"
              :class="{
                'border-blue-500 bg-blue-50': selectedPreset?.name === preset.name,
                'hover:border-blue-300': selectedPreset?.name !== preset.name
              }"
            >
              <h3 class="font-semibold mb-2">{{ preset.name }}</h3>
              <div class="text-sm text-gray-600">{{ preset.description }}</div>
            </button>

            <!-- Custom Options Button -->
            <button
              @click="useCustomOptions"
              class="p-4 border rounded-lg text-left transition-colors duration-200"
              :class="{
                'border-blue-500 bg-blue-50': isCustom,
                'hover:border-blue-300': !isCustom
              }"
            >
              <h3 class="font-semibold mb-2">Opciones customizadas</h3>
              <div class="text-sm text-gray-600">Crea opciones por default customizadas</div>
            </button>
          </div>

          <!-- Custom Options Editor -->
          <div v-if="isCustom" class="border-t pt-4">
            <h3 class="font-semibold mb-3">Custom Options</h3>
            <div class="space-y-3">
              <div v-for="(option, index) in customOptions" :key="index" 
                   class="flex items-center gap-3">
                <input
                  v-model="option.text"
                  placeholder="Option text"
                  class="flex-1 p-2 border rounded"
                />
                <select v-model="option.type" class="p-2 border rounded">
                  <option value="radio">Radio Button</option>
                  <option value="input">Input Field</option>
                </select>
                <input
                  v-if="option.type === 'input'"
                  v-model="option.unit"
                  placeholder="Unit"
                  class="w-24 p-2 border rounded"
                />
                <button @click="removeCustomOption(index)" 
                        class="text-red-500 hover:text-red-700">
                  <span class="material-icons">delete</span>
                </button>
              </div>
              <button
                @click="addCustomOption"
                class="w-full p-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:text-blue-500"
              >
                + Add Option
              </button>
            </div>
          </div>

          <!-- Preview Section -->
          <div v-if="selectedPreset || isCustom" class="border-t pt-4">
            <h3 class="font-semibold mb-3">Vista Previa</h3>
            <div class="bg-gray-50 p-4 rounded-lg">
              <div v-for="option in previewOptions" :key="option.id" class="mb-2 last:mb-0">
                <template v-if="option.type === 'radio'">
                  <label class="inline-flex items-center">
                    <input type="radio" class="mr-2" disabled>
                    {{ option.text }}
                  </label>
                </template>
                <template v-else-if="option.type === 'input'">
                  <div class="flex items-center gap-2">
                    <span>{{ option.text }}:</span>
                    <input type="text" class="border rounded px-2 py-1 w-20" disabled>
                    <span>{{ option.unit }}</span>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer - Always visible -->
      <div class="sticky bottom-0 bg-white pt-4 mt-6 border-t">
        <div class="flex justify-end gap-4">
          <button
            @click="cancel"
            class="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            @click="confirmSelection"
            :disabled="!canConfirm"
            class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Continue with Selected Options
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.overflow-y-auto {
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
}

/* Prevent body scroll when modal is open */
:deep(body.modal-open) {
  overflow: hidden;
}
</style>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { v4 as uuidv4 } from 'uuid';

export default {
  name: 'DefaultOptionsSelector',
  emits: ['select', 'cancel'],
  
  setup(props, { emit }) {
    const selectedPreset = ref(null);
    const isCustom = ref(false);
    const customOptions = ref([]);

    const optionPresets = [
      {
        name: 'Bueno / Regular / Malo / No Aplica',
        description: '4 opciones para calificar el estado de un elemento',
        options: [
          { id: uuidv4(), text: 'Bueno', type: 'radio' },
          { id: uuidv4(), text: 'Regular', type: 'radio' },
          { id: uuidv4(), text: 'Malo', type: 'radio' },
          { id: uuidv4(), text: 'No Aplica', type: 'radio' }
        ]
      }
    ];

    const addCustomOption = () => {
      customOptions.value.push({
        id: uuidv4(),
        text: '',
        type: 'radio',
        unit: ''
      });
    };

    const removeCustomOption = (index) => {
      customOptions.value.splice(index, 1);
    };

    const useCustomOptions = () => {
      isCustom.value = true;
      selectedPreset.value = null;
      if (customOptions.value.length === 0) {
        addCustomOption();
      }
    };

    const selectPreset = (preset) => {
      selectedPreset.value = preset;
      isCustom.value = false;
    };

    const previewOptions = computed(() => {
      if (isCustom.value) return customOptions.value;
      return selectedPreset.value ? selectedPreset.value.options : [];
    });

    const canConfirm = computed(() => {
      if (isCustom.value) {
        return customOptions.value.length > 0 && 
               customOptions.value.every(opt => opt.text.trim());
      }
      return !!selectedPreset.value;
    });

    const confirmSelection = () => {
      const options = isCustom.value ? customOptions.value : selectedPreset.value.options;
      emit('select', options.map(opt => ({
        ...opt,
        id: uuidv4()
      })));
    };

    const cancel = () => {
      emit('cancel');
    };

    // Add body class when component mounts
    onMounted(() => {
      document.body.classList.add('modal-open');
    });

    // Remove body class when component unmounts
    onUnmounted(() => {
      document.body.classList.remove('modal-open');
    });

    return {
      selectedPreset,
      isCustom,
      customOptions,
      optionPresets,
      previewOptions,
      canConfirm,
      selectPreset,
      useCustomOptions,
      addCustomOption,
      removeCustomOption,
      confirmSelection,
      cancel
    };
  }
};
</script>
